import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { LanguageProvider } from './context/LanguageContext';
import { Toaster } from 'sonner';
import { Landing } from './pages/Landing';
import { LandingPublic } from './pages/LandingPublic';
import { AuthCallback } from './pages/AuthCallback';
import { Dashboard } from './pages/Dashboard';
import { Profile } from './pages/Profile';
import { Publish } from './pages/Publish';
import { MyProperties } from './pages/MyProperties';
import { EditProperty } from './pages/EditProperty';
import { Knowledge } from './pages/Knowledge';
import { KnowledgeDetail } from './pages/KnowledgeDetail';
import { Invest } from './pages/Invest';
import { PropertyDetail } from './pages/PropertyDetail';
import { Messages } from './pages/Messages';
import { SelectPlan } from './pages/SelectPlan';
import { CRM } from './pages/CRM';
import { Tasks } from './pages/Tasks';
import { Community } from './pages/Community';
import { AdminDashboard } from './pages/AdminDashboard';
import { UserManagement } from './pages/UserManagement';
import { Organizations } from './pages/Organizations';
import { VideoManagement } from './pages/VideoManagement';
import { ProtectedRoute } from './components/ProtectedRoute';
import { Layout } from './components/Layout';
import '@/App.css';
import { Admin } from './pages/Admin';
import { Privacidad } from './pages/Privacidad';
import { Cookies } from './pages/Cookies';
import { Contacto } from './pages/Contacto';
import { Agentes } from './pages/Agentes';
import { registerServiceWorker, requestNotificationPermission, suscribirAPush } from './utils/notifications';

function AppRouter() {
  const location = useLocation();
  
  if (location.hash?.includes('session_id=') || location.hash?.includes('access_token=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Landing pública — ehomesp.com */}
      <Route path="/" element={<LandingPublic />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/agentes" element={<Agentes />} />
      <Route path="/privacidad" element={<Privacidad />} />
      <Route path="/cookies" element={<Cookies />} />
      <Route path="/contacto" element={<Contacto />} />
      {/* Login / acceso a la app */}
      <Route path="/login" element={<Landing />} />
      
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <Layout>
            <Dashboard />
          </Layout>
        </ProtectedRoute>
      } />
      
      <Route path="/select-plan" element={
        <ProtectedRoute>
          <SelectPlan />
        </ProtectedRoute>
      } />
      
      <Route path="/profile" element={
        <ProtectedRoute>
          <Layout>
            <Profile />
          </Layout>
        </ProtectedRoute>
      } />
      
      <Route path="/publish" element={
        <ProtectedRoute>
          <Layout>
            <Publish />
          </Layout>
        </ProtectedRoute>
      } />
      
      <Route path="/my-properties" element={
        <ProtectedRoute>
          <Layout>
            <MyProperties />
          </Layout>
        </ProtectedRoute>
      } />
      
      <Route path="/edit-property/:id" element={
        <ProtectedRoute>
          <Layout>
            <EditProperty />
          </Layout>
        </ProtectedRoute>
      } />
      
      <Route path="/knowledge" element={
        <ProtectedRoute requiredPlan="essential">
          <Layout>
            <Knowledge />
          </Layout>
        </ProtectedRoute>
      } />
      
      <Route path="/knowledge/:id" element={
        <ProtectedRoute requiredPlan="essential">
          <Layout>
            <KnowledgeDetail />
          </Layout>
        </ProtectedRoute>
      } />
      
      <Route path="/invest" element={
        <ProtectedRoute requiredPlan="essential">
          <Layout>
            <Invest />
          </Layout>
        </ProtectedRoute>
      } />
      
      <Route path="/messages" element={
        <ProtectedRoute>
          <Layout>
            <Messages />
          </Layout>
        </ProtectedRoute>
      } />
      
      <Route path="/property/:id" element={
        <ProtectedRoute>
          <Layout>
            <PropertyDetail />
          </Layout>
        </ProtectedRoute>
      } />
      
      <Route path="/crm" element={
        <ProtectedRoute requiredPlan="premium">
          <Layout>
            <CRM />
          </Layout>
        </ProtectedRoute>
      } />
      
      <Route path="/tasks" element={
        <ProtectedRoute requiredPlan="premium">
          <Layout>
            <Tasks />
          </Layout>
        </ProtectedRoute>
      } />
      
      <Route path="/community" element={
        <ProtectedRoute requiredPlan="essential">
          <Layout>
            <Community />
          </Layout>
        </ProtectedRoute>
      } />
      
      {/* Admin routes */}
      <Route path="/admin" element={
        <ProtectedRoute>
          <Layout>
            <Admin />
          </Layout>
        </ProtectedRoute>
      } />
      
      <Route path="/admin/dashboard" element={
        <ProtectedRoute>
          <Layout>
            <AdminDashboard />
          </Layout>
        </ProtectedRoute>
      } />
      
      <Route path="/admin/users" element={
        <ProtectedRoute>
          <Layout>
            <UserManagement />
          </Layout>
        </ProtectedRoute>
      } />
      
      <Route path="/admin/organizations" element={
        <ProtectedRoute>
          <Layout>
            <Organizations />
          </Layout>
        </ProtectedRoute>
      } />
      
      <Route path="/admin/videos" element={
        <ProtectedRoute>
          <Layout>
            <VideoManagement />
          </Layout>
        </ProtectedRoute>
      } />
    </Routes>
  );
}

function App() {
  // Register Service Worker and request notification permission
  useEffect(() => {
    registerServiceWorker();
    // Request permission after 3 seconds to avoid blocking initial load
    setTimeout(async () => {
      const permitido = await requestNotificationPermission();
      // Dar de alta el móvil solo si ha dicho que sí. Si todavía no hay sesión
      // devuelve 401 y no pasa nada: se vuelve a intentar en la siguiente carga.
      if (permitido) suscribirAPush();
    }, 3000);
  }, []);

  return (
    <BrowserRouter>
      <AuthProvider>
        <ThemeProvider>
          <LanguageProvider>
            <AppRouter />
            <Toaster 
              position="top-right" 
              expand={false}
              richColors
              closeButton
            />
          </LanguageProvider>
        </ThemeProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
